import {
  ChatComposer,
  type ChatComposerValueChangeReason,
} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {SUBMIT_ERROR_MESSAGE} from '@github-ui/ahp-session-client/submission-error'
import {announce} from '@github-ui/aria-live'
import {ChatInputValidationError} from '@github-ui/copilot-chat-state/validation-error'
import {RepoPicker} from '@github-ui/agent-sessions-interface/components/RepoPicker'
import {createScopedRepositoryLoader} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import {MAX_AGENT_TASK_PROMPT_LENGTH} from '@github-ui/copilot-shared-ui/composer/max-prompt-length'
import {AttachmentErrorBanner} from '@github-ui/copilot-shared-ui/components/AttachmentErrorBanner'
import {ChatReferenceAutocomplete} from '@github-ui/copilot-shared-ui/components/ChatReferenceAutocomplete'
import {FilePickerPanel} from '@github-ui/copilot-shared-ui/components/FilePickerPanel'
import {fetchRepos} from '@github-ui/copilot-shared-ui/repos/fetch-repos'
import {useCallback, useMemo, useRef, useState, type ReactNode, type RefObject} from 'react'

import {SpacePicker} from '../spaces/SpacePicker'
import {EmptyState, suggestionContextForAttachment} from '../existing-session/EmptyState'
import {
  SELECTED_SPACE_ATTACHMENT_ID,
  hasSubmittableAttachments,
  selectedSpaceAttachment,
} from '../spaces/space-attachment'
import {EnvironmentAttachmentControls, EnvironmentImageAttachments} from './EnvironmentAttachments'
import {ComposerToolbar} from './ComposerToolbar'
import {useComposerFileAttach} from '@github-ui/copilot-shared-ui/hooks/use-composer-file-attach'
import {
  useComposerReferenceActivation,
  type ComposerActivationTarget,
} from '@github-ui/copilot-shared-ui/hooks/use-composer-reference-activation'
import {repoPickerScopeSubtitle} from './shared'
import {isImplicitChipId} from './use-implicit-draft-attachments'
import type {TaskComposerController} from './use-task-composer'
import styles from './CopilotComposerInput.module.css'

export interface CopilotComposerInputProps {
  controller: TaskComposerController
  value: string
  onValueChange: (value: string | ((current: string) => string), reason?: ChatComposerValueChangeReason) => void
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  disabled: boolean
  readOnly: boolean
  emptyState?: 'embedded'
  containerClassName?: string
  submitWithModifier: boolean
  clearOnSubmit: boolean
  disableWhileSubmitting: boolean
  maxTextareaHeight?: number
  /**
   * The premium-request meter, supplied by the owner that knows whether the
   * draft's environment draws on that quota. Omitted where it does not apply.
   */
  meter?: ReactNode
  leadingControl?: ReactNode
  /** Opts the toolbar pickers into the composer's container-query compaction. */
  responsiveIconOnly?: boolean
  disableAutoFocus?: boolean
  /** Content that should remain between the composer and its feedback banners. */
  belowComposerContent?: ReactNode
}

/**
 * The composing surface for a coding task: the environment's attachments,
 * image handling, repo picker, and the task toolbar.
 *
 * Everything it shows is driven by the task composer controller, which the new
 * session composer owns — the chat and task surfaces share one draft, so this
 * takes the controller rather than building its own.
 */
export function CopilotComposerInput({
  controller,
  value,
  onValueChange,
  textAreaRef,
  disabled,
  readOnly,
  emptyState,
  containerClassName,
  submitWithModifier,
  clearOnSubmit,
  disableWhileSubmitting,
  maxTextareaHeight,
  meter,
  leadingControl,
  responsiveIconOnly,
  disableAutoFocus,
  belowComposerContent,
}: CopilotComposerInputProps) {
  const {
    onSubmit: submit,
    isAhpEnvironment,
    isSandboxDraft,
    environmentAttachments,
    implicitAttachments = [],
    attachmentScopeOwner,
    pickerScopeOwner,
    implicitChips,
    dismissImplicitChip,
    attachReferenceUrl,
    onPaste,
    dropTargetProps,
    isDraggedOver,
    targetSelection,
    selectedSpace,
    setSelectedSpace,
    isSpaceResolving,
    setIsSpaceResolving,
    spaceSelectionError,
    setSpaceSelectionError,
  } = controller
  const isChat = targetSelection.kind === 'chat'
  useComposerFileAttach(environmentAttachments, isAhpEnvironment && !readOnly && !disabled)
  const scopedRepoLoader = useMemo(
    () =>
      createScopedRepositoryLoader((query, signal) => fetchRepos(query, signal, pickerScopeOwner), pickerScopeOwner),
    [pickerScopeOwner],
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachButtonRef = useRef<HTMLButtonElement>(null)
  const [submissionError, setSubmissionError] = useState<string>()
  const openUploadDialog = useCallback(() => fileInputRef.current?.click(), [])
  // The selected Space (a display-only scope indicator) and the page the draft
  // was composed on are shown beside what the user attached by hand, so the
  // first turn says what it will carry before it is sent. The Space picker lives
  // inside the attach ("+") menu, which closes on selection and unmounts its
  // label, so the token keeps the chosen Space visible while composing.
  const spaceToken = useMemo(
    () => (isChat ? selectedSpaceAttachment(selectedSpace) : undefined),
    [isChat, selectedSpace],
  )
  const composerAttachments = useMemo(
    () => [
      ...(spaceToken ? [spaceToken] : []),
      ...(isAhpEnvironment ? environmentAttachments.attachments : []),
      ...implicitChips,
    ],
    [spaceToken, environmentAttachments.attachments, implicitChips, isAhpEnvironment],
  )
  const handleRemoveAttachment = useCallback(
    (id: string) => {
      if (id === SELECTED_SPACE_ATTACHMENT_ID) {
        setSelectedSpace(undefined)
        return
      }
      if (isImplicitChipId(id)) {
        dismissImplicitChip(id)
        return
      }
      environmentAttachments.handleRemoveAttachment(id)
    },
    [dismissImplicitChip, environmentAttachments, setSelectedSpace],
  )
  const activationTargets = useMemo<ComposerActivationTarget[]>(
    () => [
      ...implicitChips.flatMap((chip, index) => {
        const attachment = implicitAttachments[index]
        return attachment ? [{id: chip.id, target: {kind: 'attachment' as const, attachment}}] : []
      }),
      ...(selectedSpace
        ? [
            {
              id: SELECTED_SPACE_ATTACHMENT_ID,
              target: {kind: 'space' as const, owner: selectedSpace.owner, number: selectedSpace.id},
            },
          ]
        : []),
    ],
    [implicitAttachments, implicitChips, selectedSpace],
  )
  const referenceActivation = useComposerReferenceActivation(
    environmentAttachments,
    composerAttachments,
    activationTargets,
  )
  const onSubmit = useCallback(
    async (content: string) => {
      setSubmissionError(undefined)
      try {
        return (await submit(content)) !== false
      } catch (error) {
        const message = error instanceof ChatInputValidationError ? error.message : SUBMIT_ERROR_MESSAGE
        setSubmissionError(message)
        announce(message)
        return false
      }
    },
    [submit],
  )
  const latestExplicitAttachment = environmentAttachments.tracked?.at(-1)?.ahp
  const suggestionContext = latestExplicitAttachment
    ? suggestionContextForAttachment(latestExplicitAttachment)
    : (environmentAttachments.selectedRepos?.length ?? 0) > 0
      ? 'repository'
      : suggestionContextForAttachment(implicitAttachments.at(-1))

  const imagePreview =
    isAhpEnvironment && environmentAttachments.images.length > 0 ? (
      <EnvironmentImageAttachments controller={environmentAttachments} />
    ) : undefined

  return (
    <>
      {emptyState === 'embedded' && !readOnly && !disabled ? (
        <EmptyState
          suggestionContext={suggestionContext}
          onSelectSuggestion={async suggestion => {
            await onSubmit((suggestion.prompt ?? suggestion.question).trim())
          }}
        />
      ) : null}
      <div className={isDraggedOver ? styles.isDraggedOver : undefined} {...dropTargetProps}>
        <ChatComposer
          value={value}
          onValueChange={onValueChange}
          textAreaRef={textAreaRef}
          onSubmit={onSubmit}
          placeholder={isChat ? 'Send a message' : 'Give Copilot a background task to work on'}
          ariaLabel={isChat ? 'Send a message' : 'Coding task prompt'}
          containerClassName={containerClassName}
          inputTextSize="responsive"
          submitWithModifier={submitWithModifier}
          clearOnSubmit={clearOnSubmit}
          disableWhileSubmitting={disableWhileSubmitting || isAhpEnvironment}
          maxLength={MAX_AGENT_TASK_PROMPT_LENGTH}
          maxTextareaHeight={maxTextareaHeight}
          disableAutoFocus={disableAutoFocus}
          onPaste={isAhpEnvironment ? environmentAttachments.handlePaste : onPaste}
          attachments={referenceActivation.attachments}
          hasAttachedContent={Boolean(
            hasSubmittableAttachments(composerAttachments) ||
            (isAhpEnvironment && environmentAttachments.hasAttachments),
          )}
          {...(isAhpEnvironment
            ? {
                onFilesAdded: environmentAttachments.handleFilesAdded,
                onAttachFiles: environmentAttachments.openFilePicker,
                onUploadFiles: openUploadDialog,
                onAttachRepo: environmentAttachments.openRepoPicker,
                attachButtonRef,
                ...(imagePreview ? {attachmentPreview: imagePreview} : {}),
              }
            : {})}
          attachMenuContent={
            isAhpEnvironment ? (
              <SpacePicker
                selectedSpace={selectedSpace}
                onSelectSpace={setSelectedSpace}
                isResolving={isSpaceResolving}
                onResolvingChange={setIsSpaceResolving}
                selectionError={spaceSelectionError}
                onSelectionErrorChange={setSpaceSelectionError}
                disabled={!isSandboxDraft}
                disabledDescription="Spaces can only be attached when starting a new sandbox."
              />
            ) : undefined
          }
          onRemoveAttachment={handleRemoveAttachment}
          onAttachmentClick={referenceActivation.onAttachmentClick}
          renderInput={textarea => (
            <ChatReferenceAutocomplete
              disabled={!isAhpEnvironment || readOnly}
              scopeOwner={pickerScopeOwner}
              repositoryLoader={scopedRepoLoader}
              selectedRepos={environmentAttachments.selectedRepos}
              onUpdateRepos={environmentAttachments.handleUpdateRepos}
              onFilePickerSelect={environmentAttachments.handleFilePickerSelect}
              attachReferenceUrl={attachReferenceUrl}
              restrictionOwner={attachmentScopeOwner ?? undefined}
            >
              {textarea}
            </ChatReferenceAutocomplete>
          )}
          disabled={disabled}
          readOnly={readOnly}
          toolbarContent={
            readOnly ? undefined : (
              <ComposerToolbar
                controller={controller}
                leadingControl={leadingControl}
                responsiveIconOnly={responsiveIconOnly}
              />
            )
          }
          trailingToolbarContent={readOnly ? undefined : meter}
        />
      </div>
      {belowComposerContent}
      {submissionError && (
        <AttachmentErrorBanner
          messages={[submissionError]}
          onDismiss={() => setSubmissionError(undefined)}
          title="Submission error"
        />
      )}
      {isAhpEnvironment && (
        <>
          <AttachmentErrorBanner
            messages={environmentAttachments.rejectionMessages}
            onDismiss={environmentAttachments.dismissRejections}
          />
          <EnvironmentAttachmentControls controller={environmentAttachments} fileInputRef={fileInputRef} />
          {environmentAttachments.filePickerOpen && (
            <FilePickerPanel
              key={`files:${pickerScopeOwner ?? 'unresolved'}`}
              isOpen
              onClose={environmentAttachments.closeFilePicker}
              onSelect={environmentAttachments.handleFilePickerSelect}
              repositoryLoader={scopedRepoLoader}
              scopeOwner={pickerScopeOwner}
            />
          )}
          <RepoPicker
            key={`repos:${pickerScopeOwner ?? 'unresolved'}`}
            anchorRef={attachButtonRef}
            repositoryLoader={scopedRepoLoader}
            selectedRepos={environmentAttachments.selectedRepos}
            onUpdateRepos={environmentAttachments.handleUpdateRepos}
            triggerOpen={environmentAttachments.repoPickerOpen}
            onClose={environmentAttachments.closeRepoPicker}
            anchorSide="outside-top"
            subtitle={repoPickerScopeSubtitle(pickerScopeOwner, controller.resolvedUserLogin)}
            restrictionOwner={controller.scopeOwner ?? undefined}
          />
        </>
      )}
    </>
  )
}

CopilotComposerInput.displayName = 'CopilotComposerInput'
